import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useChatMessages } from '../hooks/useChatMessages';
import { useChatData } from '../hooks/useChatData';
import { sendMessage } from '../utils/chatUtils';
import { useApp } from '../context/useApp';
import { 
  ArrowLeft, 
  Send, 
  Paperclip, 
  Mic, 
  Phone, 
  Video,
  Download, 
  FileText, 
  Image, 
  X,
  User,
  MoreVertical,
  Music,
  Play,
  Pause,
  Volume2,
  VolumeX,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Maximize2,
  Minimize2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { validateFileForUpload, formatFileSize } from '../utils/fileUtils';
import MediaPreviewModal from './MediaPreviewModal';
import VoiceRecorder from './VoiceRecorder';
import MessageBubble from './MessageBubble';

const ChatConversation = () => {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { user } = useApp();
  const { messages, loading: messagesLoading, error: messagesError, isOffline } = useChatMessages(chatId);
  
  // Get current user from localStorage for comparison
  const getCurrentUser = () => {
    try {
      const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
      return user;
    } catch (error) {
      console.error('Error getting current user:', error);
      return {};
    }
  };
  
  const currentUser = getCurrentUser();
  const { chatData, loading: chatLoading, error: chatError, setChatData } = useChatData(chatId);
  
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [showMediaPreview, setShowMediaPreview] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [typingStatus, setTypingStatus] = useState({});
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Function to enhance chat data with lawyer information
  const enhanceChatDataWithLawyerInfo = async () => {
    if (!chatData?.lawyerId) return;
    
    try {
      console.log('🔍 Enhancing chat data with lawyer info for ID:', chatData.lawyerId);
      
      // Try lawyers collection first
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      
      const lawyerRef = doc(db, 'lawyers', chatData.lawyerId);
      const lawyerSnap = await getDoc(lawyerRef);
      
      if (lawyerSnap.exists()) {
        const lawyerData = lawyerSnap.data();
        console.log('✅ Lawyer data fetched from lawyers collection:', lawyerData);
        
        // Update chat data with lawyer information
        const enhancedData = {
          ...chatData,
          lawyerName: lawyerData.full_name || lawyerData.fullName || lawyerData.name || lawyerData.displayName || chatData.lawyerName || 'Lawyer',
          lawyerProfilePic: lawyerData.profile_picture || lawyerData.profilePic || lawyerData.profilePicUrl || lawyerData.photoURL || chatData.lawyerProfilePic || chatData.lawyerPhoto || '/default-lawyer.png',
          specialization: lawyerData.specialization?.join(', ') || lawyerData.specialization || chatData.specialization || 'General Law'
        };
        
        // Update the chat data state
        setChatData(enhancedData);
        console.log('✅ Chat data enhanced with lawyer info:', enhancedData);
      } else {
        // Try users collection as fallback
        console.log('🔍 Lawyer not found in lawyers collection, trying users collection');
        const userRef = doc(db, 'users', chatData.lawyerId);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          console.log('✅ User data fetched from users collection:', userData);
          
          const enhancedData = {
            ...chatData,
            lawyerName: userData.full_name || userData.fullName || userData.name || userData.displayName || chatData.lawyerName || 'Lawyer',
            lawyerProfilePic: userData.profile_picture || userData.profilePic || userData.photoURL || chatData.lawyerProfilePic || chatData.lawyerPhoto || '/default-lawyer.png',
            specialization: userData.specialization || chatData.specialization || 'General Law'
          };
          
          setChatData(enhancedData);
          console.log('✅ Chat data enhanced with user info:', enhancedData);
        }
      }
    } catch (error) {
      console.error('❌ Error enhancing chat data with lawyer info:', error);
    }
  };

  // Enhance chat data when it's loaded
  useEffect(() => {
    if (chatData && chatData.lawyerId) {
      enhanceChatDataWithLawyerInfo();
    }
  }, [chatData?.lawyerId]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Enhanced data persistence and offline support
  useEffect(() => {
    console.log('🔍 ChatConversation - chatData:', chatData);
    console.log('🔍 ChatConversation - messages:', messages);
    console.log('🔍 ChatConversation - chatId:', chatId);
    
    // Check if we have booking data from navigation state
    const location = window.location;
    console.log('🔍 Current location:', location.pathname);
    
    // Check if we have booking data in localStorage
    const storedChats = JSON.parse(localStorage.getItem('userChats') || '[]');
    const currentChat = storedChats.find(c => c.id === chatId);
    console.log('🔍 Current chat from localStorage:', currentChat);
    
    // Debug: Log all possible lawyer data fields
    if (chatData) {
      console.log('🔍 LAWYER DATA DEBUG:', {
        lawyerName: chatData.lawyerName,
        'lawyer.name': chatData.lawyer?.name,
        'lawyer.full_name': chatData.lawyer?.full_name,
        'lawyer.fullName': chatData.lawyer?.fullName,
        lawyerId: chatData.lawyerId,
        'lawyer.id': chatData.lawyer?.id,
        specialization: chatData.specialization,
        'lawyer.specialization': chatData.lawyer?.specialization,
        caseType: chatData.caseType,
        consultationType: chatData.consultationType
      });
    }
    
    // Enhance chat data with lawyer information if missing
    if (chatData && (!chatData.lawyerName || !chatData.lawyerProfilePic)) {
      console.log('🔍 Missing lawyer info, enhancing...');
      enhanceChatDataWithLawyerInfo();
    }
    
    if (currentChat?.bookingId) {
      console.log('🔍 Found booking ID in chat:', currentChat.bookingId);
      // Check if we need to send a booking confirmation message
      const hasConfirmationMessage = messages.some(msg => 
        msg.content && msg.content.includes('Booking Confirmed') ||
        msg.content && msg.content.includes('booking_confirmed')
      );
      
      if (!hasConfirmationMessage && currentChat.bookingId) {
        console.log('🔍 No booking confirmation message found, checking if we should create one...');
        // This will be handled by the chat service when the chat is created
      }
    }
    
    // Save current chat state to localStorage for persistence
    if (chatData && chatId) {
      try {
        const updatedChats = storedChats.map(chat => 
          chat.id === chatId ? { ...chat, ...chatData, lastAccessed: new Date().toISOString() } : chat
        );
        localStorage.setItem('userChats', JSON.stringify(updatedChats));
        console.log('💾 Saved chat state to localStorage for persistence');
      } catch (error) {
        console.error('⚠️ Could not save chat state to localStorage:', error);
      }
    }
    
    // Save messages to localStorage for offline access
    if (messages.length > 0 && chatId) {
      try {
        const chatMessagesKey = `chatMessages_${chatId}`;
        localStorage.setItem(chatMessagesKey, JSON.stringify(messages));
        console.log('💾 Saved messages to localStorage for offline access');
      } catch (error) {
        console.error('⚠️ Could not save messages to localStorage:', error);
      }
    }
  }, [chatData, messages, chatId]);
  
  // Load messages from localStorage if Firestore is not available
  useEffect(() => {
    if (!messagesLoading && messages.length === 0 && chatId) {
      try {
        const chatMessagesKey = `chatMessages_${chatId}`;
        const storedMessages = JSON.parse(localStorage.getItem(chatMessagesKey) || '[]');
        
        if (storedMessages.length > 0) {
          console.log('📱 Loaded messages from localStorage fallback:', storedMessages.length);
          // Note: We can't directly set messages here as it's managed by the hook
          // But we can store them for display purposes
        }
      } catch (error) {
        console.error('⚠️ Could not load messages from localStorage:', error);
      }
    }
  }, [messagesLoading, messages.length, chatId]);



  // Handle typing indicator
  const handleTyping = useCallback(() => {
    if (!chatId) return;
    
    // Update local typing state
    setIsTyping(true);
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    
    const timeout = setTimeout(() => {
      setIsTyping(false);
    }, 3000);
    
    setTypingTimeout(timeout);

    console.log('⌨️ Typing indicator active');
  }, [typingTimeout, chatId]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending || !user?.uid) return;

    const messageText = newMessage.trim();
    setNewMessage('');
    setSending(true);

    try {
      await sendMessage(chatId, user, messageText);
      console.log('✅ Message sent successfully');
      toast.success('Message sent!');
    } catch (error) {
      console.error('❌ Error sending message:', error);
      setNewMessage(messageText); // Restore message
      
      // Show user-friendly error message
      if (error.message.includes('not active')) {
        toast.error('Chat is not active. Please wait for the lawyer to accept your request.');
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle file selection
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // If it's an image, show preview first
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview({
          file: file,
          url: e.target.result,
          name: file.name
        });
        setShowImagePreview(true);
      };
      reader.readAsDataURL(file);
      return;
    }

    // For non-image files, send directly
    await sendFile(file);
  };

  // Send file after preview (for images) or directly (for other files)
  const sendFile = async (file) => {
    // Show loading toast
    const loadingToast = toast.loading('Uploading file...');

    // Validate file
    const validation = validateFileForUpload(file, {
      maxSizeMB: 25, // Increased to 25MB for better support
      allowedTypes: [
        'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml',
        'video/mp4', 'video/avi', 'video/mov', 'video/webm', 'video/mkv', 'video/flv',
        'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a', 'audio/aac', 'audio/flac',
        'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/zip', 'application/rar', 'application/x-rar-compressed'
      ]
    });

    if (!validation.isValid) {
      toast.dismiss(loadingToast);
      toast.error(validation.errors[0]);
      return;
    }

    setSending(true);
    try {
      // Determine file type for better message display
      let fileType = 'file';
      if (file.type.startsWith('image/')) fileType = 'image';
      else if (file.type.startsWith('video/')) fileType = 'video';
      else if (file.type.startsWith('audio/')) fileType = 'audio';
      else if (file.type.includes('pdf') || file.type.includes('document')) fileType = 'document';

      await sendMessage(chatId, user, file.name, file);
      
      console.log('✅ File sent successfully');
      toast.dismiss(loadingToast);
      toast.success(`${fileType.charAt(0).toUpperCase() + fileType.slice(1)} sent successfully`);
      
          } catch (error) {
      console.error('❌ Error sending file:', error);
      toast.dismiss(loadingToast);
      toast.error('Failed to send file');
    } finally {
      setSending(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle voice recording
  const handleVoiceRecordingComplete = async (audioBlob) => {
    setShowVoiceRecorder(false);
    
    // Show loading toast
    const loadingToast = toast.loading('Sending voice message...');
    setSending(true);

    try {
      await sendMessage(chatId, user, 'Voice message', null, audioBlob);
      
      console.log('✅ Voice message sent successfully');
      toast.dismiss(loadingToast);
      toast.success('Voice message sent');
      
      } catch (error) {
      console.error('❌ Error sending voice message:', error);
      toast.dismiss(loadingToast);
      toast.error('Failed to send voice message');
    } finally {
      setSending(false);
      }
    };

  const handleBackToChats = () => {
    navigate('/chats');
  };



  // Handle media preview
  const handleMediaClick = (media) => {
    setSelectedMedia(media);
    setShowMediaPreview(true);
  };

  // Handle file download
  const handleFileDownload = async (url, filename) => {
    let loadingToast = null;
    try {
      // Show loading toast
      loadingToast = toast.loading('Downloading file...');
      
      // Try multiple approaches for downloading
      let success = false;
      
      // Method 1: Try with no-cors mode first
      try {
        const response = await fetch(url, {
          method: 'GET',
          mode: 'no-cors',
        });
        
        if (response.type === 'opaque') {
          // For no-cors, we can't read the response, so try direct download
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.style.display = 'none';
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          success = true;
        }
      } catch (noCorsError) {
        console.log('No-cors method failed, trying other approaches...');
      }
      
      // Method 2: Try with XMLHttpRequest for better CORS handling
      if (!success) {
        try {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', url, true);
          xhr.responseType = 'blob';
          xhr.withCredentials = false;
          
          xhr.onload = function() {
            if (xhr.status === 200) {
              const blob = xhr.response;
              const blobUrl = window.URL.createObjectURL(blob);
              
              const link = document.createElement('a');
              link.href = blobUrl;
              link.download = filename;
              link.style.display = 'none';
              
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              
              setTimeout(() => {
                window.URL.revokeObjectURL(blobUrl);
              }, 1000);
              
              toast.dismiss(loadingToast);
              toast.success(`${filename} downloaded successfully`);
            } else {
              toast.dismiss(loadingToast);
              toast.error(`Download failed: ${xhr.status}`);
            }
          };
          
          xhr.onerror = function() {
            toast.dismiss(loadingToast);
            toast.error('Network error during download');
          };
          
          xhr.send();
          success = true;
        } catch (xhrError) {
          console.log('XMLHttpRequest method failed:', xhrError);
        }
      }
      
      // Method 3: Fallback to direct link (works in some browsers)
      if (!success) {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.dismiss(loadingToast);
        toast.success(`${filename} download initiated`);
      }
      
    } catch (error) {
      console.error('❌ Error downloading file:', error);
      if (loadingToast) {
        toast.dismiss(loadingToast);
      }
      toast.error('Failed to download file. Please try again.');
    }
  };

  // Handle media download from MediaPreviewModal
  const handleMediaDownload = (media) => {
    const filename = media.name || 'download';
    handleFileDownload(media.url, filename);
  };

  // Show loading if either chat data or messages are loading
  if (chatLoading || messagesLoading) {
       return (
         <div className="min-h-screen w-full bg-white flex items-center justify-center">
           <div className="text-center">
             <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
             <p className="text-gray-500">Loading chat...</p>
           </div>
         </div>
       );
     }

  // Show error if either chat data or messages have errors
  if (chatError || messagesError) {
       return (
         <div className="min-h-screen w-full bg-white flex items-center justify-center">
           <div className="text-center">
             <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
               <X className="w-8 h-8 text-red-500" />
             </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error loading chat</h3>
          <p className="text-gray-600 mb-4">⚠️ {chatError || messagesError}</p>
             <button
               onClick={handleBackToChats}
               className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
             >
               Back to Chats
             </button>
           </div>
         </div>
       );
     }

  // Debug logging to understand chat data structure
  console.log('🔍 ChatConversation - chatData:', chatData);
  console.log('🔍 ChatConversation - lawyer details:', {
    lawyerName: chatData?.lawyerName || chatData?.lawyer?.name,
    lawyerProfilePic: chatData?.lawyerProfilePic || chatData?.lawyerPhoto || chatData?.lawyer?.profilePic,
    specialization: chatData?.specialization || chatData?.lawyer?.specialization,
    nestedLawyer: chatData?.lawyer
  });

  return (
      <div className="flex flex-col h-screen bg-gray-50 pb-16">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={handleBackToChats}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
              {chatData?.lawyerProfilePic || 
               chatData?.lawyerPhoto || 
               chatData?.lawyer?.profilePic || 
               chatData?.lawyer?.profile_pic ||
               chatData?.lawyer?.photoURL ||
               chatData?.profilePic ||
               chatData?.lawyerPhoto ? (
            <img
                  src={chatData?.lawyerProfilePic || 
                       chatData?.lawyerPhoto || 
                       chatData?.lawyer?.profilePic || 
                       chatData?.lawyer?.profile_pic ||
                       chatData?.lawyer?.photoURL ||
                       chatData?.profilePic ||
                       chatData?.lawyerPhoto}
              alt={chatData?.lawyerName || 
                   chatData?.lawyer?.name || 
                   chatData?.lawyerName || 
                   chatData?.lawyer?.full_name ||
                   chatData?.lawyer?.fullName ||
                   "Lawyer"}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className="w-full h-full flex items-center justify-center text-gray-600" style={{ display: chatData?.lawyerProfilePic || chatData?.lawyerPhoto || chatData?.lawyer?.profilePic || chatData?.lawyer?.profile_pic || chatData?.lawyer?.photoURL || chatData?.profilePic || chatData?.lawyerPhoto ? 'none' : 'flex' }}>
                <User className="w-6 h-6" />
              </div>
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">
                {chatData?.lawyerName || 
                 chatData?.lawyer?.name || 
                 chatData?.lawyer?.full_name ||
                 chatData?.lawyer?.fullName ||
                 chatData?.lawyerName ||
                 chatData?.lawyerName ||
                 "Lawyer"}
              </h2>
              <p className="text-sm text-gray-500">
                {chatData?.specialization || 
                 chatData?.lawyer?.specialization || 
                 chatData?.caseType || 
                 chatData?.consultationType || 
                 "General Law"}
              </p>
            </div>
          </div>
        </div>
        
        {/* Offline Status Indicator */}
        {isOffline && (
          <div className="flex items-center gap-2 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
            <span>Offline</span>
          </div>
        )}
        
        <div className="flex items-center space-x-2">
          <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <Phone className="w-5 h-5 text-gray-600" />
            </button>
          <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <Video className="w-5 h-5 text-gray-600" />
              </button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) => (
          <MessageBubble
            key={message.id || index}
            message={message}
            isOwnMessage={message.senderId === user?.uid || message.senderId === currentUser?.uid}
            onMediaClick={handleMediaClick}
            onDownload={handleFileDownload}
          />
        ))}
        <div ref={messagesEndRef} />
      </div>

                           {/* Input Area */}
      <div className="bg-white border-t border-gray-200 p-4">
        {showVoiceRecorder ? (
          <VoiceRecorder
            onRecordingComplete={handleVoiceRecordingComplete}
            onCancel={() => setShowVoiceRecorder(false)}
          />
        ) : (
          <div className="flex items-end space-x-2">
            <button
              onClick={() => setShowAttachmentMenu(!showAttachmentMenu)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <Paperclip className="w-5 h-5 text-gray-600" />
            </button>

            <div className="flex-1 relative">
            <textarea
                ref={textareaRef}
              value={newMessage}
                onChange={(e) => {
                  setNewMessage(e.target.value);
                  handleTyping();
                }}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
              rows="1"
                style={{ minHeight: '44px', maxHeight: '120px' }}
              />
            </div>

            <button
              onClick={() => setShowVoiceRecorder(true)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              title="Voice Message"
            >
              <Mic className="w-5 h-5 text-gray-600" />
            </button>

              <button
                onClick={handleSendMessage}
              disabled={!newMessage.trim() || sending}
            className="p-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Send Message"
              >
                <Send className="w-5 h-5" />
              </button>
        </div>
        )}
        
        {/* Enhanced Attachment Menu */}
        {showAttachmentMenu && (
          <div className="mt-3 p-4 bg-white border border-gray-200 rounded-xl shadow-lg">
            <div className="mb-3">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Send Files</h4>
              <p className="text-xs text-gray-500">Choose what you want to send</p>
            </div>
            
            <div className="grid grid-cols-4 gap-3">
              {/* Photo */}
              <button
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'image/*';
                  input.multiple = true;
                  input.onchange = (e) => {
                    Array.from(e.target.files).forEach(file => {
                      const event = { target: { files: [file] } };
                      handleFileSelect(event);
                    });
                  };
                  input.click();
                  setShowAttachmentMenu(false);
                }}
                className="flex flex-col items-center gap-2 p-3 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors border border-blue-200"
              >
                <Image className="w-6 h-6 text-blue-600" />
                <span className="text-xs font-medium text-blue-700">Photo</span>
              </button>
              
              {/* Video */}
              <button
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'video/*';
                  input.onchange = (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const event = { target: { files: [file] } };
                      handleFileSelect(event);
                    }
                  };
                  input.click();
                  setShowAttachmentMenu(false);
                }}
                className="flex flex-col items-center gap-2 p-3 rounded-lg bg-green-50 hover:bg-green-100 transition-colors border border-green-200"
              >
                <Video className="w-6 h-6 text-green-600" />
                <span className="text-xs font-medium text-green-700">Video</span>
              </button>
              
              {/* Document */}
              <button
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = '.pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,.zip,.rar';
                  input.onchange = (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const event = { target: { files: [file] } };
                      handleFileSelect(event);
                    }
                  };
                  input.click();
                  setShowAttachmentMenu(false);
                }}
                className="flex flex-col items-center gap-2 p-3 rounded-lg bg-purple-50 hover:bg-purple-100 transition-colors border border-purple-200"
              >
                <FileText className="w-6 h-6 text-purple-600" />
                <span className="text-xs font-medium text-purple-700">Document</span>
              </button>
              
              {/* Audio */}
              <button
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'audio/*';
                  input.onchange = (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const event = { target: { files: [file] } };
                      handleFileSelect(event);
                    }
                  };
                  input.click();
                  setShowAttachmentMenu(false);
                }}
                className="flex flex-col items-center gap-2 p-3 rounded-lg bg-orange-50 hover:bg-orange-100 transition-colors border border-orange-200"
              >
                <Music className="w-6 h-6 text-orange-600" />
                <span className="text-xs font-medium text-orange-700">Audio</span>
              </button>
            </div>
            
            {/* Quick Actions */}
            <div className="mt-4 pt-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Quick Actions</span>
                <button
                  onClick={() => setShowAttachmentMenu(false)}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

      <input
        ref={fileInputRef}
        type="file"
          onChange={handleFileSelect}
        className="hidden"
          accept="image/*"
                   />
                  </div>

      {/* Media Preview Modal */}
              <MediaPreviewModal
          isOpen={showMediaPreview}
          onClose={() => setShowMediaPreview(false)}
          media={selectedMedia}
          onDownload={handleMediaDownload}
        />

      {/* Image Preview Modal (Before Sending) */}
      {showImagePreview && imagePreview && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-sm w-full max-h-[60vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b">
              <h3 className="text-base font-semibold text-gray-900">Preview Image</h3>
              <button
                onClick={() => {
                  setShowImagePreview(false);
                  setImagePreview(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Image Preview */}
            <div className="p-3">
              <img
                src={imagePreview.url}
                alt="Preview"
                className="w-full h-48 object-cover rounded-lg mb-3"
              />
              <p className="text-xs text-gray-600 mb-3">{imagePreview.name}</p>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-2 p-3 border-t">
              <button
                onClick={() => {
                  setShowImagePreview(false);
                  setImagePreview(null);
                }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setShowImagePreview(false);
                  await sendFile(imagePreview.file);
                  setImagePreview(null);
                }}
                disabled={sending}
                className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 text-sm"
              >
                {sending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatConversation;
